import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { profilesKeys } from "./useProfiles";

/**
 * Renvoie une invitation BRANDÉE à un utilisateur déjà existant.
 *
 * Passe par l'Edge Function `invite-user` en `mode: 'resend'` :
 *  - L'Edge Function génère un lien `recovery` (compte existant) vers
 *    /reset-password et envoie l'email brandé Skyline via Postmark.
 *  - La session de l'admin courant n'est PAS modifiée — seul le destinataire
 *    se logge en cliquant sur le lien dans son inbox.
 *
 * (Remplace l'ancien `signInWithOtp` qui envoyait le magic link Supabase nu.)
 */
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) => {
      const trimmed = email.trim();
      if (!trimmed) throw new Error("Email vide");
      return invokeInviteUser({ email: trimmed, mode: "resend" });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: profilesKeys.list() }),
  });
}

export interface InviteUserPayload {
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  telephone?: string;
  societe?: string;
  specialites?: string[];
  mode?: "create" | "resend";
}

export interface InviteUserResult {
  ok: true;
  user_id?: string;
  email: string;
  emailSent: boolean;
  message: string;
}

/**
 * Appelle l'Edge Function `invite-user` et déballe le vrai message d'erreur
 * (supabase-js masque le body d'erreur derrière un message générique).
 */
async function invokeInviteUser(payload: InviteUserPayload): Promise<InviteUserResult> {
  const { data, error } = await supabase.functions.invoke("invite-user", {
    body: payload,
  });
  if (error) {
    let detail = error.message;
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.text === "function") {
      try {
        const body = await ctx.text();
        const parsed = JSON.parse(body);
        if (parsed?.error) detail = `${error.message} — ${parsed.error}`;
      } catch {
        // body non-JSON, garde message générique
      }
    }
    throw new Error(detail);
  }
  if (data?.error) throw new Error(data.error);
  return data as InviteUserResult;
}

/**
 * Invite un nouvel utilisateur via l'Edge Function `invite-user`.
 * L'Edge Function :
 *  - Vérifie que l'appelant est admin (via JWT)
 *  - Crée un user Supabase Auth + envoie un magic link
 *  - Crée/upsert la row profile correspondante
 *
 * Pour que ça marche :
 *  1. Deploy : `supabase functions deploy invite-user`
 *  2. Dashboard → Edge Functions → invite-user → Secrets → ajouter SUPABASE_SERVICE_ROLE_KEY
 *  3. Appelle ce hook depuis l'UI admin
 */
export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InviteUserPayload) =>
      invokeInviteUser({ ...payload, mode: payload.mode ?? "create" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: profilesKeys.list() }),
  });
}
